import { useEffect, useRef } from 'react';

/**
 * useHistoryDismiss:
 * Synchronizes modal open state with the browser history stack.
 * When `isOpen` becomes true, pushes a unique entry to history.
 * If user presses hardware/browser Back (popstate), invokes `onDismiss()`.
 * If closed programmatically or unmounted, pops the entry back.
 */
export function useHistoryDismiss(
  isOpen: boolean,
  onDismiss: () => void,
  id: string = 'sheet'
) {
  const isPushedRef = useRef(false);
  const onDismissRef = useRef(onDismiss);
  onDismissRef.current = onDismiss;

  useEffect(() => {
    if (typeof window === 'undefined') return;

    if (isOpen) {
      const stateKey = `dismiss_${id}_${Date.now()}`;
      window.history.pushState({ [stateKey]: true }, '');
      isPushedRef.current = true;

      const handlePopState = () => {
        isPushedRef.current = false;
        onDismissRef.current();
      };

      window.addEventListener('popstate', handlePopState, { once: true });

      return () => {
        window.removeEventListener('popstate', handlePopState);
        if (isPushedRef.current) {
          isPushedRef.current = false;
          window.history.back();
        }
      };
    }
  }, [isOpen, id]);
}
